import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Mail,
  Send,
  Clock,
  Eye,
  Reply,
  AlertCircle,
  TrendingUp,
  Users,
  Calendar,
  Search,
  MoreHorizontal,
  Archive,
  Trash2,
  Star,
  Flag
} from 'lucide-react';
import DataTable from '../../components/DataTable/DataTable';
import { Breadcrumb } from '../../components/Common';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { emailService } from '../../services/emailService';
import '../../components/ConfirmationModal/ConfirmationModal.css';

const Emails = () => {
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEmails: 0,
    sentEmails: 0,
    deliveredEmails: 0,
    openedEmails: 0,
    repliedEmails: 0,
    bouncedEmails: 0,
    draftEmails: 0,
    scheduledEmails: 0,
    avgOpenRate: 0,
    avgClickRate: 0,
    avgDeliveryRate: 0
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    priority: '',
    category: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const hasLoadedRef = useRef(false);
  const hasShownEmptyToastRef = useRef(false);

  const columns = [
    {
      key: 'emailCode',
      label: 'Code',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-2">
          <Mail size={14} className="text-blue-600" />
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      ),
    },
    {
      key: 'subject',
      label: 'Subject',
      sortable: true,
      render: (value, row) => (
        <div className="max-w-xs">
          <div className="font-medium text-gray-900 truncate">{value}</div>
          {row.emailBody && (
            <div className="text-sm text-gray-500 truncate">
              {row.emailBody.substring(0, 100)}...
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'emailType',
      label: 'Type',
      sortable: true,
      render: (value) => {
        const typeColors = {
          'Outbound': 'bg-blue-100 text-blue-800',
          'Inbound': 'bg-green-100 text-green-800',
          'Follow-up': 'bg-yellow-100 text-yellow-800',
          'Marketing': 'bg-purple-100 text-purple-800',
          'Support': 'bg-orange-100 text-orange-800',
          'Internal': 'bg-gray-100 text-gray-800',
          'Automated': 'bg-indigo-100 text-indigo-800'
        };
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        );
      },
    },
    {
      key: 'emailStatus',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'Draft': 'bg-gray-100 text-gray-800',
          'Scheduled': 'bg-yellow-100 text-yellow-800',
          'Sent': 'bg-blue-100 text-blue-800',
          'Delivered': 'bg-green-100 text-green-800',
          'Opened': 'bg-indigo-100 text-indigo-800',
          'Replied': 'bg-purple-100 text-purple-800',
          'Bounced': 'bg-red-100 text-red-800',
          'Failed': 'bg-red-100 text-red-800',
          'Cancelled': 'bg-gray-100 text-gray-800'
        };
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        );
      },
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (value) => {
        const priorityColors = {
          'High': 'bg-red-100 text-red-800',
          'Medium': 'bg-yellow-100 text-yellow-800',
          'Low': 'bg-green-100 text-green-800'
        };
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        );
      },
    },
    {
      key: 'toEmails',
      label: 'Recipients',
      sortable: false,
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value && value.length > 0 ? (
            <div>
              <div className="font-medium">{value[0].name || value[0].email}</div>
              {value.length > 1 && (
                <div className="text-gray-500">+{value.length - 1} more</div>
              )}
            </div>
          ) : (
            <span className="text-gray-400">No recipients</span>
          )}
        </div>
      ),
    },
    {
      key: 'emailDate',
      label: 'Date',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-gray-900">
          <div>{new Date(value).toLocaleDateString()}</div>
          <div className="text-gray-500">{new Date(value).toLocaleTimeString()}</div>
        </div>
      ),
    },
    {
      key: 'emailOwner',
      label: 'Owner',
      sortable: true,
      render: (value) => (
        <div className="text-gray-900">{value?.firstName} {value?.lastName || 'N/A'}</div>
      ),
    },
    {
      key: 'analytics',
      label: 'Engagement',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          {row.openCount > 0 && (
            <div className="flex items-center text-green-600" title={`${row.openCount} opens`}>
              <Eye size={12} />
              <span className="text-xs ml-1">{row.openCount}</span>
            </div>
          )}
          {row.clickCount > 0 && (
            <div className="flex items-center text-blue-600" title={`${row.clickCount} clicks`}>
              <TrendingUp size={12} />
              <span className="text-xs ml-1">{row.clickCount}</span>
            </div>
          )}
          {row.emailStatus === 'Replied' && (
            <div className="flex items-center text-purple-600" title="Replied">
              <Reply size={12} />
            </div>
          )}
        </div>
      ),
    }
  ];

  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadData();
      hasLoadedRef.current = true;
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [emailsResponse, statsResponse] = await Promise.all([
        emailService.getAllEmails(),
        emailService.getEmailStats(),
      ]);

      if (emailsResponse.success) {
        setData(emailsResponse.data.docs || []);
        if (emailsResponse.data.docs.length === 0 && !hasShownEmptyToastRef.current) {
          toast.info('No emails found. Create your first email to get started.');
          hasShownEmptyToastRef.current = true;
        }
      } else {
        toast.error(emailsResponse.message || 'Failed to load emails');
      }

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load email data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate('/crm/emails/new');
  };

  const handleEdit = (row) => {
    navigate('/crm/emails/edit', { state: { emailData: row } });
  };

  const handleView = (row) => {
    navigate('/crm/emails/view', { state: { emailData: row } });
  };

  const handleDelete = (row) => {
    setDeleteItem(row);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    try {
      const response = await emailService.deleteEmail(deleteItem._id);
      if (response.success) {
        toast.success('Email deleted successfully');
        loadData();
        setShowDeleteModal(false);
        setDeleteItem(null);
      } else {
        toast.error(response.message || 'Failed to delete email');
      }
    } catch (error) {
      console.error('Error deleting email:', error);
      toast.error('Failed to delete email');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select emails to delete');
      return;
    }

    try {
      const response = await emailService.bulkDeleteEmails(selectedRows);
      if (response.success) {
        toast.success(response.message);
        setSelectedRows([]);
        loadData();
      } else {
        toast.error(response.message || 'Failed to delete emails');
      }
    } catch (error) {
      console.error('Error bulk deleting emails:', error);
      toast.error('Failed to delete emails');
    }
  };

  const handleSend = async (row) => {
    try {
      const response = await emailService.sendEmail(row._id);
      if (response.success) {
        toast.success('Email sent successfully');
        loadData();
      } else {
        toast.error(response.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const response = await emailService.searchEmails(searchTerm, filters);
      if (response.success) {
        setData(response.data.docs || []);
      } else {
        toast.error(response.message || 'Search failed');
      }
    } catch (error) {
      console.error('Error searching emails:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/dashboard' },
          { label: 'CRM', path: '/crm' },
          { label: 'Emails' },
        ]}
      />

      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title">
            <h1>Emails</h1>
            <p>Manage and track all email communications</p>
          </div>
          <div className="page-actions" style={{ marginLeft: 'auto' }}>
            <button className="btn-primary" onClick={handleAdd}>
              <Plus size={16} />
              Compose Email
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Mail size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalEmails || 0}</div>
          <div className="metric-title">Total Emails</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Send size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.sentEmails || 0}</div>
          <div className="metric-title">Sent</div>
          <div className="metric-subtitle">Emails delivered</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Eye size={16} />
            </div>
            <div className="metric-trend neutral">
              <TrendingUp size={12} />
              <span>Stable</span>
            </div>
          </div>
          <div className="metric-value">{stats.openedEmails || 0}</div>
          <div className="metric-title">Opened</div>
          <div className="metric-subtitle">Email opens</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Reply size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{stats.repliedEmails || 0}</div>
          <div className="metric-title">Replied</div>
          <div className="metric-subtitle">Email responses</div>
        </div>
      </div>


      {/* Email List */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          title="Email Communications"
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search emails..."
          emptyMessage="No emails found. Create your first email to get started."
          customActions={[
            {
              label: 'Send',
              icon: Send,
              onClick: handleSend,
              condition: (row) => row.emailStatus === 'Draft' || row.emailStatus === 'Scheduled'
            }
          ]}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Email"
        message={`Are you sure you want to delete email "${deleteItem?.subject}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Emails;
